import type { ActionNodeData } from '../nodes/ActionNode';
import type { ValidationError } from './workflowValidator';
import { safeCastString, safeCastNumber, safeCastArray, isPlainRecord } from './typeGuards';

const LOG_IN_CRM_LOGGING_ACTIVITIES = 'activities';
const LOG_IN_CRM_LOGGING_NOTES = 'notes';

function hydrateLogInCrmLoggingPreference(
    raw: unknown
): ActionNodeData['loggingPreference'] | undefined {
    if (raw === true || raw === false) {
        return raw;
    }
    const normalized = safeCastString(raw)?.trim().toLowerCase() ?? '';
    if (normalized === '') {
        return undefined;
    }
    if (normalized === LOG_IN_CRM_LOGGING_ACTIVITIES) {
        return LOG_IN_CRM_LOGGING_ACTIVITIES;
    }
    if (normalized === 'true') {
        return true;
    }
    if (normalized === 'false') {
        return false;
    }
    if (normalized === LOG_IN_CRM_LOGGING_NOTES) {
        return LOG_IN_CRM_LOGGING_NOTES;
    }
    return LOG_IN_CRM_LOGGING_NOTES;
}

function serializeLogInCrmLoggingPreference(
    value: ActionNodeData['loggingPreference']
): string | boolean {
    if (value === LOG_IN_CRM_LOGGING_ACTIVITIES) {
        return LOG_IN_CRM_LOGGING_ACTIVITIES;
    }
    if (value === true || value === 'true') {
        return true;
    }
    if (value === false || value === 'false') {
        return false;
    }
    if (value === LOG_IN_CRM_LOGGING_NOTES) {
        return LOG_IN_CRM_LOGGING_NOTES;
    }
    return true;
}

export interface ActionStrategy {
    hydrate: (config: Record<string, unknown>) => Partial<ActionNodeData>;
    serialize: (data: ActionNodeData) => Record<string, unknown>;
    validate: (data: ActionNodeData, stepId: string, title: string) => ValidationError[];
}

export const ACTION_STRATEGIES: Record<string, ActionStrategy> = {
    'Action.Message.AutomatedResponse': {
        hydrate: (config) => {
            const mediaFileList = safeCastArray(config.mediaFileList);
            return {
                message: safeCastString(config.messageBody ?? config.message),
                templateId: safeCastString(config.templateId),
                templateName: safeCastString(config.templateName ?? config.friendlyName),
                templateIntegId: safeCastString(config.templateIntegId),
                mediaFileList,
                hasMedia: mediaFileList.length > 0,
                components: config.components,
                fieldNameAndValueMap: isPlainRecord(config.fieldNameAndValueMap)
                    ? config.fieldNameAndValueMap
                    : undefined,
            };
        },
        serialize: (data) => ({
            from: '',
            pageId: '',
            to: '',
            messageBody: data.message ?? '',
            mediaFileList: data.mediaFileList ?? [],
            userId: '',
            associatedObjectId: '',
            associatedObjectType: '',
            templateId: data.templateId ?? '',
            templateName: data.templateName ?? '',
            templateIntegId: data.templateIntegId ?? '',
            components: data.components,
            fieldNameAndValueMap: data.fieldNameAndValueMap,
        }),
        validate: (data, stepId, title) => {
            if (!data.templateId && !data.message?.trim() && !data.hasMedia) {
                return [{ stepId, stepTitle: title, message: 'Enter a response message or select a template.' }];
            }
            return [];
        },
    },

    'Action.Notify.NotifyUser': {
        hydrate: (config) => ({
            recordOwner: config.includeRecordOwner === true,
            accountOwner: config.includeAccountOwner === true,
            includeAdditionalEmails: safeCastArray(config.customEmails).length > 0,
            additionalEmails: safeCastArray(config.customEmails),
        }),
        serialize: (data) => ({
            userIds: [],
            includeAccountOwner: data.accountOwner ?? false,
            includeRecordOwner: data.recordOwner ?? false,
            customEmails: data.includeAdditionalEmails ? data.additionalEmails ?? [] : [],
        }),
        validate: (data, stepId, title) => {
            const hasRecipient =
                data.recordOwner ||
                data.accountOwner ||
                (data.includeAdditionalEmails && (data.additionalEmails?.length ?? 0) > 0);
            if (!hasRecipient) {
                return [
                    {
                        stepId,
                        stepTitle: title,
                        message:
                            'Select at least one email recipient (Record Owner, Account Owner, or Additional Email Addresses).',
                    },
                ];
            }
            if (
                data.includeAdditionalEmails &&
                (!data.additionalEmails || data.additionalEmails.length === 0)
            ) {
                return [
                    {
                        stepId,
                        stepTitle: title,
                        message: 'Add at least one email address when Additional Email Addresses is selected.',
                    },
                ];
            }
            return [];
        },
    },

    'Action.Crm.LogInCrm': {
        hydrate: (config) => {
            const loggingPreference = hydrateLogInCrmLoggingPreference(config.loggingPreference);
            return {
                ...(loggingPreference !== undefined ? { loggingPreference } : {}),
                logRecordsFor:
                    safeCastString(config.logRecordsFor)?.trim().toLowerCase() === 'recent' ? 'recent' : 'all',
                logRecordsOnStatus: safeCastString(config.logRecordsOnStatus) ?? '',
                activityType: safeCastString(config.activityType) ?? '',
                logRecordAsThread:
                    config.logRecordAsThread === true ||
                    config.shouldThreadActivities === true ||
                    safeCastString(config.logRecordAsThread) === 'true'
                        ? 'true'
                        : 'false',
            };
        },
        serialize: (data) => {
            const v = data.loggingPreference;
            const loggingPreferenceSerialized = serializeLogInCrmLoggingPreference(v);
            const isActivities = v === LOG_IN_CRM_LOGGING_ACTIVITIES;
            const logRecordsFor = data.logRecordsFor === 'recent' ? 'recent' : 'all';
            const thread = data.logRecordAsThread === 'true';
            const status = (safeCastString(data.logRecordsOnStatus) ?? '').trim();
            const activity = (safeCastString(data.activityType) ?? '').trim();

            const out: Record<string, unknown> = {
                loggingPreference: loggingPreferenceSerialized,
                logRecordsFor,
                logRecordAsThread: isActivities ? thread : false,
                shouldThreadActivities: isActivities ? thread : false,
            };
            if (status) {
                out.logRecordsOnStatus = status;
            }
            if (isActivities && activity) {
                out.activityType = activity;
            }
            return out;
        },
        validate: (data, stepId, title) => {
            const errors: ValidationError[] = [];
            const loggingPreferenceValue = data.loggingPreference;
            const isLoggingPreferenceAllowed =
                loggingPreferenceValue === undefined ||
                typeof loggingPreferenceValue === 'boolean' ||
                loggingPreferenceValue === LOG_IN_CRM_LOGGING_NOTES ||
                loggingPreferenceValue === LOG_IN_CRM_LOGGING_ACTIVITIES ||
                loggingPreferenceValue === 'true' ||
                loggingPreferenceValue === 'false';
            if (!isLoggingPreferenceAllowed) {
                errors.push({
                    stepId,
                    stepTitle: title,
                    message: 'Choose Notes or Activities for CRM logging.',
                });
            }
            const logRecordsForValue = data.logRecordsFor;
            if (logRecordsForValue !== undefined && logRecordsForValue !== 'all' && logRecordsForValue !== 'recent') {
                errors.push({
                    stepId,
                    stepTitle: title,
                    message: 'Apply log to records must be all matching records or most recent only.',
                });
            }
            return errors;
        },
    },

    'Action.Crm.CreateLead': {
        hydrate: (config) => ({
            createLead: config.enabled === false ? 'false' : 'true',
        }),
        serialize: (data) => ({
            enabled: data.createLead !== 'false',
        }),
        validate: () => [],
    },

    'Action.FlowControl.Delay': {
        hydrate: (config) => {
            const amount = safeCastNumber(config.delayAmount) ?? 0;
            const unit = safeCastString(config.delayUnit)?.toLowerCase() ?? 'minutes';
            const totalMinutes =
                unit === 'days' ? amount * 24 * 60 : unit === 'hours' ? amount * 60 : amount;
            return {
                delayDays: Math.floor(totalMinutes / (24 * 60)),
                delayHours: Math.floor((totalMinutes % (24 * 60)) / 60),
                delayMinutes: Math.floor(totalMinutes % 60),
            };
        },
        serialize: (data) => {
            const days = data.delayDays ?? 0;
            const hours = data.delayHours ?? 0;
            const minutes = data.delayMinutes ?? 0;
            const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
            return { delayAmount: totalMinutes, delayUnit: 'minutes' };
        },
        validate: (data, stepId, title) => {
            const days = data.delayDays ?? 0;
            const hours = data.delayHours ?? 0;
            const minutes = data.delayMinutes ?? 0;
            const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
            const MIN_DELAY_MINUTES = 15;
            const MAX_DELAY_MINUTES = 30 * 24 * 60;
            if (totalMinutes < MIN_DELAY_MINUTES) {
                return [{ stepId, stepTitle: title, message: 'Delay must be at least 15 minutes.' }];
            }
            if (totalMinutes > MAX_DELAY_MINUTES) {
                return [{ stepId, stepTitle: title, message: 'Delay cannot exceed 30 days.' }];
            }
            return [];
        },
    },
};

export function getActionStrategy(actionCode: string | undefined): ActionStrategy | undefined {
    return actionCode ? ACTION_STRATEGIES[actionCode] : undefined;
}
